// Admin panel API çağrıları, services/api.js'teki hostname bazlı seçim deseniyle aynı.
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost');
const MEMBER_API = isLocal ? 'http://localhost:5290' : 'https://member.api.hirethink.ai';
const CV_API = isLocal ? 'http://localhost:5293' : 'https://cv.api.hirethink.ai';
// Aday yüzü, isbulsun.com'un kendisi (admin.isbulsun.com ile farklı origin,
// bu yüzden impersonation token'ı localStorage değil URL hash ile taşınır).
export const CANDIDATE_APP_URL = isLocal ? 'http://localhost:3000' : 'https://isbulsun.com';

function getAdminToken() {
  return localStorage.getItem('admin_token');
}

async function postJson(path, body) {
  const res = await fetch(`${MEMBER_API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return { ok: res.ok, httpStatus: res.status, ...json };
}

// admin.hirethink.ai ile paylaşılan aynı login, ayrı bir admin kullanıcı/şifre
// sistemi yok. Yetki kontrolü backend'de: sadece izinli hesaplar admin API'lerinden
// veri alabilir, diğerleri 403 görür. FE email whitelist'i tutmaz.
// Yanıt iki şekilde gelebilir: data.token (2FA yok/atlandı, giriş tamam) veya
// data.isTwoFactor:true + data.tokenId (SMS koduyla CodeVerify'a geçilmeli).
// Bu ayrım yalnızca response'tan okunur, email'e göre dallanma YAPILMAZ.
export async function adminLogin(email, password) {
  return postJson('/api/Auth/Login', { email, password });
}

// SMS ile gelen 6 haneli kodu doğrular. Kod yanlış/süresi dolmuşsa BE 404 döner.
export async function adminVerifyCode(tokenId, code) {
  return postJson('/api/Auth/CodeVerify', { tokenId, code });
}

// Yalnızca 401 (token geçersiz/süresi dolmuş) tüm oturumu kapatıp login
// ekranına atar. 403 (bu kaynağa yetkisi yok) artık GLOBAL logout tetiklemez,
// önceden herhangi bir uç (örn. yeni eklenen tek bir admin alt-özelliği) 403
// dönünce tüm paneli kapatıyordu; bu, o uca özgü bir yetki eksikliğinde bile
// admini panelin tamamından atıyordu. Şimdi 403'ü çağıran sayfa kendi satır
// içi hata mesajıyla (res.message) gösterir, oturum ayakta kalır.
export async function adminAuthFetch(url, options = {}) {
  const token = getAdminToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && token) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    window.dispatchEvent(
      new CustomEvent('admin-logout', { detail: { reason: 'unauthorized' } })
    );
  }

  return res;
}

async function getJson(url) {
  const res = await adminAuthFetch(url);

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return { ok: res.ok, httpStatus: res.status, ...json };
}

async function putJson(url, body) {
  const res = await adminAuthFetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return { ok: res.ok, httpStatus: res.status, ...json };
}

// isbulsun.com üzerinden kayıt olmuş adaylar, CV API, Source parametresiyle filtreli.
// Yanıt: BaseResponseModel<List<CandidateSourceListResponseModel>>
// { data: [{ id, name, surname, email, phone, companyId, companyName, source, createdAt }], status, message, total }
export async function getCandidateListBySource(source = 'isbulsun.com') {
  return getJson(`${CV_API}/api/Candidate/GetCandidateListBySource?Source=${encodeURIComponent(source)}`);
}

// Tek adayın tam profili (eğitim, deneyim, başvurular, mülakatlar dahil),
// aday listesindeki veya GetPaymentListBySource'taki candidateId ile çağrılır,
// Süper Admin yetkisiyle çalışır. Alan şeması teyit edilmediği için
// AdminCandidateDetailPage gelen HER alanı otomatik/genel bir şekilde render eder.
export async function getCandidateFullDetail(id) {
  return getJson(`${CV_API}/api/Candidate/GetCandidateFullDetail?Id=${encodeURIComponent(id)}`);
}

// Ödeme listesi, Members API, Süper Admin. Source boşsa tüm kaynaklar
// (career.sebnemramadan.com dahil) döner, doluysa yalnızca o kaynak filtrelenir.
// Yanıt: BaseResponseModel<List<CandidatePaymentListResponseModel>>
export async function getPaymentListBySource(source = '') {
  const query = source ? `?Source=${encodeURIComponent(source)}` : '';
  return getJson(`${MEMBER_API}/api/Payment/GetPaymentListBySource${query}`);
}

// Aboneliği admin adına iptal eder (ve istenirse Iyzico'dan gerçek para iadesi
// dener), Members API, Süper Admin. `id`, GetPaymentListBySource satırındaki
// `subscriptionId`. Yalnızca "Aktif" abonelikler için çalışır.
// status:true + data:true ama message doluysa: iptal oldu ama iade
// başarısız, Iyzico panelinden manuel kontrol gerekir, mesajı göster.
export async function adminCancelSubscription({ id, refundPayment = false, reason } = {}) {
  const res = await adminAuthFetch(`${MEMBER_API}/api/Payment/AdminCancelSubscription`, {
    method: 'POST',
    body: JSON.stringify({ id, refundPayment, reason: reason || null }),
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return { ok: res.ok, httpStatus: res.status, ...json };
}

// Impersonation, adayın normal login token'ıyla AYNI şekilde çalışan bir JWT
// döner. Members API, yalnızca Süper Admin. Admin'in kendi token'ının üzerine
// YAZILMAZ; çağıran taraf (AdminCandidateDetailPage) bu token'ı yeni bir
// sekmede isbulsun.com'a taşır.
export async function impersonateCandidate(candidateId) {
  return getJson(`${MEMBER_API}/api/CandidateLogin/ImpersonateCandidate?Id=${encodeURIComponent(candidateId)}`);
}

// Aktif ilanlar (HireThink Kariyer + jooble vb. dış kaynaklardan toplananlar,
// temp_job tablosu), CV API, admin-özel /api/Isbulsun controller'ı (yalnızca
// Süper Admin, diğerleri 403 görür). Auth header dışında parametre gerekmiyor,
// en yeni ilan en üstte döner.
export async function getJobList() {
  return getJson(`${CV_API}/api/Isbulsun/GetJobList`);
}

// Tek ilanın tam detayı, bulunamazsa status:false + message, data:null döner.
// `id`, GetJobList'ten dönen kaydın "id" alanı (temp_job.id) olmalı, slug
// bu uçta sorgu parametresi olarak çalışmaz.
export async function getJobDetail(id) {
  return getJson(`${CV_API}/api/Isbulsun/GetJobDetail?Id=${encodeURIComponent(id)}`);
}

// Kısmi güncelleme, yalnızca payload'da gönderilen alanlar değişir, id zorunlu.
// Dönüş: BaseResponseModel<bool>
export async function updateJob(payload) {
  return putJson(`${CV_API}/api/Isbulsun/UpdateJob`, payload);
}
